import { MeetingSourceElement, useVideoSignalDetection } from "@kalyzee/kast-web-sdk";
import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ResolvedComposition, ResolvedCompositionSlot } from "../../helpers/composition";
import { logger } from "../../helpers/logger";
import { MeetingSourceExtended } from "../../hooks/meeting";
import styles from "./SourcesComposition.module.css";

/**
 * Track ids of a stream. The SDK assigns the video and the audio track
 * separately, each in its own `track` event, into a single MediaStream instance
 * it mutates in place — so the stream never changes identity and only its
 * content says something happened.
 */
const streamSignature = (stream: MediaStream | undefined): string => (
  stream ? stream.getTracks().map((track) => `${track.kind}:${track.id}`).sort().join("|") : ""
);

interface SlotProps {
  slot: ResolvedCompositionSlot;
  source?: MeetingSourceExtended;
  stream?: MediaStream;
  /** Changes whenever the stream gains, loses or replaces a track. */
  signature?: string;
  /** The source failed to open (nothing plugged in, permission denied…). */
  errored?: boolean;
  registerVideo: (key: string, element: HTMLVideoElement | null) => void;
  onAudioBlocked: () => void;
}

const Placeholder = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.placeholder}>{children}</div>
);

const Slot = React.memo(({ slot, source, stream, signature, errored, registerVideo, onAudioBlocked }: SlotProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Signal detection is opt-in per source: without it a capture card sending
  // its test pattern is indistinguishable from a real image.
  const { enabled: detectionEnabled, ...detectionOptions } = source?.signalDetection ?? {};
  const { hasSignal } = useVideoSignalDetection(stream, {
    ...detectionOptions,
    enabled: !!detectionEnabled && !!stream,
  });
  const noSignal = !!detectionEnabled && hasSignal === false;

  const setVideo = useCallback((element: HTMLVideoElement | null) => {
    videoRef.current = element;
    registerVideo(slot.key, element);
  }, [slot.key, registerVideo]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    // Re-assigned on every track change, not only when the stream object
    // changes: the audio track lands after the video one, in the same stream
    // instance, and a media element does not render a track added to a stream
    // it is already playing — the picture would come up mute.
    video.srcObject = stream ?? null;
    if (!stream) return;
    // An unmuted video is refused until the page has been interacted with. The
    // picture must not wait for that: the overlay asks for the gesture while
    // everything else keeps playing.
    video.play().catch((err) => {
      logger.log(`[composition] autoplay refused on "${slot.key}"`, err);
      if (!video.muted) onAudioBlocked();
    });
  }, [stream, signature, slot.key, onAudioBlocked]);

  const content = () => {
    if (slot.missingSource) return <Placeholder>Source «&nbsp;{slot.missingSource}&nbsp;» introuvable</Placeholder>;
    if (!stream) return <Placeholder>{errored ? "Source non disponible" : "Ouverture de la source…"}</Placeholder>;
    return (
      <>
        <video
          ref={setVideo}
          className={styles.video}
          style={{ objectFit: slot.fit }}
          autoPlay
          playsInline
          muted={slot.muted}
        />
        {noSignal && <Placeholder>Pas de signal</Placeholder>}
      </>
    );
  };

  return (
    <div className={clsx(styles.slot, noSignal && styles.slotNoSignal)} style={slot.style}>
      {content()}
      {slot.label && <div className={styles.label}>{slot.label}</div>}
    </div>
  );
});

export interface SourcesCompositionProps {
  composition: ResolvedComposition;
  className?: string;
  /** Show the resolution errors on screen (default: true — the page often runs unattended). */
  showErrors?: boolean;
}

/**
 * Renders a composition: one hidden `MeetingSourceElement` per distinct source,
 * whose stream feeds every slot pointing at it, laid out by the composition's
 * grid template or free rectangles.
 */
export const SourcesComposition = ({ composition, className, showErrors = true }: SourcesCompositionProps) => {
  const [streams, setStreams] = useState<Record<string, { stream: MediaStream; signature: string }>>({});
  const [errored, setErrored] = useState<Record<string, boolean>>({});
  const [audioBlocked, setAudioBlocked] = useState(false);
  const videosRef = useRef(new Map<string, HTMLVideoElement>());

  const registerVideo = useCallback((key: string, element: HTMLVideoElement | null) => {
    if (element) videosRef.current.set(key, element);
    else videosRef.current.delete(key);
  }, []);

  const onAudioBlocked = useCallback(() => setAudioBlocked(true), []);

  // The gesture the browser was waiting for: retry every video at once, the
  // muted ones included (they may have been refused too).
  const resumeAudio = useCallback(() => {
    setAudioBlocked(false);
    videosRef.current.forEach((video) => {
      video.play().catch((err) => logger.log("[composition] play refused after gesture", err));
    });
  }, []);

  const setStream = useCallback((key: string, stream: MediaStream | undefined) => {
    setStreams((prev) => {
      const current = prev[key];
      if (!stream) {
        if (!current) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      // Comparing the object alone would miss the audio track arriving after
      // the video one — the SDK keeps mutating the same MediaStream.
      const signature = streamSignature(stream);
      if (current?.stream === stream && current.signature === signature) return prev;
      return { ...prev, [key]: { stream, signature } };
    });
    if (stream) setErrored((prev) => (prev[key] ? { ...prev, [key]: false } : prev));
  }, []);

  const sourcesByKey = useMemo(
    () => new Map(composition.sources.map(({ key, source }) => [key, source])),
    [composition.sources],
  );

  return (
    <div className={clsx(styles.root, className)} style={{ background: composition.background }}>
      {/* Devices are opened once here, off-screen; the slots only display the
          resulting streams. Audio playback stays with the slots — the SDK mutes
          its own players unless the device asks otherwise. */}
      {composition.sources.map(({ key, source }) => (
        <MeetingSourceElement
          key={key}
          className={styles.hiddenSource}
          source={source}
          using
          callbacks={{
            track: (_track, data) => setStream(key, data.media),
            update: (data) => setStream(key, data.media),
            destroy: () => setStream(key, undefined),
            error: (type, _ref, _data, err) => {
              logger.error(`[composition] source "${key}" (${type})`, err);
              setErrored((prev) => ({ ...prev, [key]: true }));
            },
          }}
        />
      ))}

      <div className={styles.layout} style={composition.containerStyle}>
        {composition.slots.map((slot) => (
          <Slot
            key={slot.key}
            slot={slot}
            source={slot.sourceKey ? sourcesByKey.get(slot.sourceKey) : undefined}
            stream={slot.sourceKey ? streams[slot.sourceKey]?.stream : undefined}
            signature={slot.sourceKey ? streams[slot.sourceKey]?.signature : undefined}
            errored={slot.sourceKey ? errored[slot.sourceKey] : false}
            registerVideo={registerVideo}
            onAudioBlocked={onAudioBlocked}
          />
        ))}
      </div>

      {audioBlocked && (
        <button type="button" className={styles.audioGate} onClick={resumeAudio}>
          Cliquer pour activer le son
        </button>
      )}

      {showErrors && composition.errors.length > 0 && (
        <div className={styles.errors}>
          {composition.errors.map((error) => <div key={error}>{error}</div>)}
        </div>
      )}
    </div>
  );
};
